import {
  computed,
  Component,
  inject,
  OnDestroy,
  OnInit,
  signal,
} from '@angular/core';
import { Title } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { QueryLoadingState } from '@globalconnect/query';
import { IpoTranslatePipe } from '@ip-only/front-end-library';
import { saveAs } from 'file-saver';
import { filter, map, Observable, Subject, switchMap, takeUntil } from 'rxjs';
import { InstallationReportOrderDetail } from '../../shared/installation-report-content-v2/installation-report-content-entities';
import { InstallationReportFilterService } from '../../shared/installation-report-content-v2/service/installation-report-filter.service';
import {
  onErrorDownloadExcel,
  onSuccessDownloadExcel,
} from '../../shared/installation-report-content-v2/utils/handle-gcquery-request';
import { RP_DOWNLOAD_ORDERS_OVERVIEW_EXCEL } from '../../shared/posthug-analytics/analytics-event-types';
import { AnalyticsService } from '../../shared/posthug-analytics/analytics.service';
import { ToastsService } from '../../shared/toast/toast.service';
import { isTopLevelDomainFI } from '../../shared/util/deploy-helper';
import { CONSTRUCTION_RESPONSIBLE } from '../../user-roles-def/user-roles';
import { ResourcesService } from '../../user/resources.service';
import { Resource } from '../../user/user';
import { UserService } from '../../user/user.service';
import { GroupAgreements } from '../entities/installation-reporting-mdu.entities';
import { isMduEnabled } from '../helpers/mdu-feature.helper';
import { InstallationReportingMduService } from '../service/mdu/installation-reporting-mdu.service';
import { InstallationReportingV2Service } from '../service/sdu/installation-reporting-v2.service';

@Component({
  selector: 'app-installation-reporting-wrapper',
  templateUrl: './installation-reporting-wrapper.component.html',
  styleUrls: ['./installation-reporting-wrapper.component.scss'],
  standalone: false,
})
export class InstallationReportingWrapperComponent
  implements OnInit, OnDestroy
{
  private readonly resourcesService = inject(ResourcesService);
  private readonly analyticsService = inject(AnalyticsService);
  private readonly installationReportingServiceV2 = inject(
    InstallationReportingV2Service,
  );
  private readonly mduService = inject(InstallationReportingMduService);
  private readonly toastService = inject(ToastsService);
  private readonly translate = inject(IpoTranslatePipe);
  private readonly titleService = inject(Title);
  private readonly filterService = inject(InstallationReportFilterService);
  private readonly userService = inject(UserService);
  private readonly router = inject(Router);

  ifs$: Observable<string | null>;
  installationReportingResponse = signal<
    QueryLoadingState<InstallationReportOrderDetail[]> | undefined
  >(undefined);
  groupAgreementsResponse = signal<
    QueryLoadingState<GroupAgreements> | undefined
  >(undefined);
  totalGroupAgreements = this.mduService.totalGroupAgreements;

  selectedProject$ = this.resourcesService.getSelectedResource$();
  cancelSubs$ = new Subject<void>();

  isDeliveryPortalFI: boolean = isTopLevelDomainFI();
  get canImportPlannedDates(): boolean {
    return this.userService.checkAccess([CONSTRUCTION_RESPONSIBLE]);
  }
  isInstallationReportModalOpen(): boolean {
    const path = this.router.url.split('?')[0];
    return /^\/installation-reporting\/[^/]+\/[^/]+$/.test(path);
  }

  excelLoadingState = computed(() =>
    this.isMduTabActive()
      ? this.mduService.excelLoadingState()
      : this.installationReportingServiceV2.excelLoadingState(),
  );
  filterText = '';
  filterFields = [
    'orderDetail.locationInformation.streetName',
    'orderDetail.locationInformation.city',
    'orderDetail.contactInformation.firstName',
    'orderDetail.contactInformation.lastName',
  ];
  isMduEnabled = isMduEnabled();

  constructor() {
    this.titleService.setTitle(
      this.translate.transform(this.isDeliveryPortalFI ? 'tabFI' : 'tabSE'),
    );
  }

  ngOnInit(): void {
    this.getIfs();
    this.getSelectedProject();
  }

  ngOnDestroy(): void {
    this.cancelSubs$.next();
    this.cancelSubs$.complete();
  }

  getIfs() {
    this.ifs$ = this.resourcesService
      .getSelectedResource$()
      .pipe(map((resource) => (resource ? resource.value : null)));
  }

  getSelectedProject() {
    const selectedIfs$ = this.selectedProject$.pipe(
      filter((project): project is Resource => !!project?.value),
      map((project) => project.value),
      takeUntil(this.cancelSubs$),
    );

    selectedIfs$
      .pipe(
        switchMap((ifs) =>
          this.installationReportingServiceV2.getDeliveryItems$(ifs),
        ),
      )
      .subscribe((response) =>
        this.installationReportingResponse.set(response),
      );

    if (this.isMduEnabled) {
      selectedIfs$
        .pipe(switchMap((ifs) => this.mduService.getGroupAgreements$(ifs)))
        .subscribe((response) => this.groupAgreementsResponse.set(response));
    }
  }

  onFilterTextChanged(text: string) {
    this.filterText = text;
    this.filterService.onChangePage(0);
  }

  onActiveTabChanged(index: number) {
    this.activeTabIndex.set(index);
  }

  onDownloadExcel(ifs: string) {
    const isMdu = this.isMduTabActive();
    const excelService = isMdu
      ? this.mduService
      : this.installationReportingServiceV2;
    const fileNamePrefix = isMdu ? 'group-agreements-' : 'orders-overview-';

    excelService.setExcelLoadingState(true);
    excelService.createExcelFile(ifs, {
      onSuccess: (data) => {
        onSuccessDownloadExcel(this.toastService, this.translate, () =>
          this.handleDownloadExcelResult(excelService, fileNamePrefix, data),
        );
      },
      onError: () => {
        onErrorDownloadExcel(this.toastService, this.translate, () =>
          this.handleDownloadExcelResult(excelService, fileNamePrefix),
        );
      },
    });
    this.analyticsService.logEvent(RP_DOWNLOAD_ORDERS_OVERVIEW_EXCEL, {
      ifs,
      flow: isMdu ? 'mdu' : 'sdu',
    });
  }

  handleDownloadExcelResult(
    excelService: { setExcelLoadingState: (state: boolean) => void },
    fileNamePrefix: string,
    data?: { fileName: string; file: Blob },
  ) {
    data && saveAs(data.file, fileNamePrefix + data.fileName);
    excelService.setExcelLoadingState(false);
  }

  updatePlannedInstallationDates = (ifsNumber: string, $event: Event) => {
    if ($event) {
      const input = $event.target as HTMLInputElement;
      const excelFile = (input.files as FileList)[0];
      const formdata = new FormData();
      formdata.append('excel', excelFile);

      if (this.isMduTabActive()) {
        this.mduService.updateFromExcel(ifsNumber, formdata);
      } else {
        this.installationReportingServiceV2.updatePlannedInstallationDates(
          ifsNumber,
          formdata,
        );
      }
      input.value = '';
    }
  };

  isMduSduListingLoading(): boolean {
    const sduResponse = this.installationReportingResponse();
    if (!sduResponse || sduResponse.isLoading) {
      return true;
    }
    if (!this.isMduEnabled) {
      return false;
    }
    const mduResponse = this.groupAgreementsResponse();
    return !mduResponse || mduResponse.isLoading;
  }

  resolvedGcTabIndex = computed<number>(() => {
    if (this.sduItemCount() > 0) {
      return 0;
    }

    if (this.mduItemCount() > 0) {
      return 1;
    }

    return 0;
  });

  activeTabIndex = signal<number | null>(null);

  isMduTabActive = computed<boolean>(
    () =>
      this.isMduEnabled &&
      (this.activeTabIndex() ?? this.resolvedGcTabIndex()) === 1,
  );

  showExcelButtons = computed<boolean>(
    () => !(this.isMduTabActive() && this.isDeliveryPortalFI),
  );

  sduItemCount(): number {
    return this.installationReportingResponse()?.data?.length ?? 0;
  }

  checkSduItemCount(): boolean {
    return this.sduItemCount() === 0;
  }

  mduItemCount(): number {
    return this.totalGroupAgreements() ?? 0;
  }

  checkMduItemCount(): boolean {
    return this.mduItemCount() === 0;
  }
}
